import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["count", "button", "burst", "fire", "heartIcon", "maxLabel"]
  static values = { url: String, max: Number, initialCount: Number, bandera: String }

  connect() {
    this.maxCount = Number.isFinite(this.maxValue) && this.maxValue > 0 ? this.maxValue : 10
    this.currentCount = Number.isFinite(this.initialCountValue) ? Math.max(this.initialCountValue, 0) : 0
    this.sessionClicks = 0
    this.fireVisible = false
    this.sessionMaxed = false
    if (this.hasMaxLabelTarget) this.maxLabelTarget.hidden = true
    this.updateState()
  }

  disconnect() {}

  updateState() {
    if (this.hasCountTarget) this.countTarget.textContent = String(this.currentCount)
    const reached = this.sessionMaxed
    if (this.hasButtonTarget) this.buttonTarget.disabled = reached
    this.element.classList.toggle("is-liked", this.currentCount > 0)
    this.element.classList.toggle("is-maxed", reached)
    this.element.classList.toggle("is-fire-active", reached && this.fireVisible)
    if (this.hasFireTarget) {
      this.fireTarget.hidden = !this.fireVisible
    }
    if (this.hasHeartIconTarget) this.heartIconTarget.hidden = reached
    if (this.hasMaxLabelTarget) {
      this.maxLabelTarget.hidden = !reached
      this.maxLabelTarget.textContent = `+${this.sessionClicks}`
    }
  }

  async toggle() {
    if (this.sessionMaxed) return

    const token = document.querySelector("meta[name='csrf-token']")?.content
    let response
    try {
      response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": token || "",
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ amount: 1 })
      })
    } catch {
      return
    }

    if (!response.ok) return

    await response.json()
    const nextCount = this.currentCount + 1
    const changed = nextCount !== this.currentCount
    this.currentCount = nextCount
    this.sessionClicks += 1
    this.fireVisible = this.currentCount >= this.maxCount
    this.sessionMaxed = this.sessionClicks >= this.maxCount
    this.updateState()

    if (changed) {
      this.showBurst()
      this.animatePulse()
      this.spawnConfetti()
      if (this.sessionMaxed) this.animateMaxReached()
    }
  }

  animatePulse() {
    this.element.classList.add("is-animating")
    setTimeout(() => this.element.classList.remove("is-animating"), 600)
  }

  showBurst() {
    if (!this.hasBurstTarget) return

    this.burstTarget.hidden = false
    this.burstTarget.textContent = `+${Math.min(this.sessionClicks, this.maxCount)}`
    this.burstTarget.classList.remove("is-visible")

    requestAnimationFrame(() => {
      this.burstTarget.classList.add("is-visible")
    })

    clearTimeout(this.burstTimeout)
    this.burstTimeout = setTimeout(() => {
      this.burstTarget.classList.remove("is-visible")
      this.burstTarget.hidden = true
    }, 1400)
  }

  spawnConfetti() {
    const src = this.banderaValue
    if (!src) return

    const count = Math.min(4 + this.sessionClicks * 3, 28)
    const rect = this.element.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2

    for (let i = 0; i < count; i++) {
      const el = document.createElement("img")
      el.src = src
      el.setAttribute("aria-hidden", "true")

      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 1.1
      const dist = 55 + Math.random() * 90 + this.sessionClicks * 8
      const size = 16 + Math.random() * 20
      const duration = 700 + Math.random() * 500
      const delay = Math.random() * 180
      const rotation = (Math.random() - 0.5) * 60

      Object.assign(el.style, {
        position: "fixed",
        width: `${size}px`,
        height: `${size}px`,
        left: `${cx}px`,
        top: `${cy}px`,
        transform: "translate(-50%, -50%) scale(0) rotate(0deg)",
        opacity: "1",
        zIndex: "9999",
        pointerEvents: "none",
        objectFit: "contain",
        borderRadius: "3px",
        transition: `transform ${duration}ms cubic-bezier(0.17,0.89,0.32,1.1) ${delay}ms, opacity ${Math.round(duration * 0.55)}ms ease ${delay + Math.round(duration * 0.45)}ms`,
      })

      document.body.appendChild(el)

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const tx = Math.cos(angle) * dist
          const ty = Math.sin(angle) * dist - 20
          el.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(1) rotate(${rotation}deg)`
          el.style.opacity = "0"
        })
      })

      setTimeout(() => el.remove(), delay + duration + 250)
    }
  }

  animateMaxReached() {
    this.element.classList.add("is-celebrating")
    if (this.hasCountTarget) this.countTarget.classList.add("is-flashing")

    clearTimeout(this.maxTimeout)
    this.maxTimeout = setTimeout(() => {
      this.element.classList.remove("is-celebrating")
      if (this.hasCountTarget) this.countTarget.classList.remove("is-flashing")
    }, 2400)
  }
}
